/**
 * Utility functions for handling JWT tokens
 */

interface JWTPayload {
  sub?: string;  // Subject (user ID)
  user_id?: string;
  userId?: string;
  email?: string;
  exp?: number;
  iat?: number;
  [key: string]: any;
}

/**
 * Decode a JWT token and extract the payload
 * @param token - The JWT token string
 * @returns The decoded payload or null if invalid
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    // JWT structure: header.payload.signature
    const parts = token.split('.');
    
    if (parts.length !== 3) {
      console.error('Invalid JWT: Expected 3 parts, got', parts.length);
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    
    // Base64 URL decode
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Extract user ID from JWT token
 * Tries multiple common fields: sub, user_id, userId
 * Falls back to generating a stable hash from the token if no user ID found
 * @param token - The JWT token string
 * @returns The user ID (never null)
 */
export function getUserIdFromToken(token: string): string {
  const payload = decodeJWT(token);
  
  if (!payload) {
    console.warn('Could not decode token, using token hash as user ID');
    return generateTokenHash(token);
  }

  // Try common user ID fields
  const userId = payload.sub || payload.user_id || payload.userId || payload.email;

  if (!userId) {
    console.warn('No user ID found in token payload, using token hash as user ID');
    return generateTokenHash(token);
  }

  return String(userId);
}

/**
 * Generate a stable hash from token for use as user ID
 * @param token - The JWT token string
 * @returns A stable hash string
 */
function generateTokenHash(token: string): string {
  // Use first 32 chars of base64 encoded token as stable ID
  // This will be consistent for the same token
  const hash = btoa(token.substring(0, 50))
    .replace(/[^a-zA-Z0-9]/g, '')
    .substring(0, 32);
  
  return `user_${hash}`;
}

/**
 * Check if a JWT token is expired
 * @param token - The JWT token string
 * @returns True if expired, false otherwise
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJWT(token);
  
  if (!payload || !payload.exp) {
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  
  return currentTime >= expirationTime;
}

/**
 * Log token information for debugging
 * @param token - The JWT token string
 */
export function logTokenInfo(token: string): void {
  const payload = decodeJWT(token);

  if (!payload) {
    console.error('Cannot decode token');
    return;
  }

  // Log minimal token info for debugging
  const userId = payload.sub || payload.user_id || payload.userId;
  const isExpired = payload.exp ? isTokenExpired(token) : 'unknown';

  if (process.env.NODE_ENV === 'development') {
    console.log('Token Info:', {
      userId,
      email: payload.email,
      isExpired
    });
  }
}
